import { FC, memo } from 'react';
import { useSearchParams } from 'next/navigation';
import { css } from '@bts-web/utils-style-engine';
import {
  AmountInput,
  AmountInputWrapper,
  Divider,
  SkeletonElement,
} from '../../../common';
import { useTradeForm } from './hooks/useTradeForm/useTradeForm';
import { QuickSelectButtons } from './subcomponents/QuickSelectButtons/QuickSelectButtons';
import { TARGET_PRICE_KEY, VERIFY_LINK_KEY } from '../constants';
import { TradeFormProps } from './types';
import { SetUpSavingsPlanBox } from './subcomponents/SetUpSavingsPlanBox/SetUpSavingsPlanBox';
import { VerificationLinkForm } from './subcomponents/VerificationLinkForm';
import { HiddenInputs } from './subcomponents/HiddenInputs';
import { SubmitSection } from './subcomponents/SubmitSection';
import { SubmitButtonWrapper } from '../../Reusable/SubmitButtonWrapper/SubmitButtonWrapper';
import { getAppConfig } from '@bts-web/core-app-config';
import { useFeatureFlag } from '../../../statsig/sdk/provider';
import { TradeControllerVariants, TradeOperation } from '../../utils';
import { TradeAssetSelector } from '../../TradeAssets/TradeAssetSelector';
import { MainTradeFormError } from './subcomponents/MainTradeFormError';
import { MutableNonNullableAsset } from '../types';
import { LearnMoreOrderDrawerButton } from './subcomponents/LearnMoreOrderDrawerButton/LearnMoreOrderDrawerButton';
import { OrderOperation } from '../../../orders/types';
import { useFiatWallet } from '../../Fiat/Wallet/FiatWalletContext.use-client';
import { FiatFeesSection } from '../../Fiat/Wallet/FiatFeesSection';
import { FiatAssetSwitch } from '../../../common/components/FiatAssetSwitch/FiatAssetSwitch';

const TradeFormFooter: FC<{
  baseDisabled: boolean;
  baseLoading: boolean;
  staticValues: TradeFormProps['staticValues'];
}> = ({ baseDisabled, baseLoading, staticValues }) => {
  const { isLoading } = useFiatWallet();

  const disabled = baseDisabled || isLoading;

  return (
    <SubmitButtonWrapper>
      <SubmitSection
        loading={baseLoading && !disabled}
        disabled={disabled}
        staticValues={staticValues}
      />
    </SubmitButtonWrapper>
  );
};

export const MainTradeForm: FC<TradeFormProps> = ({
  assetDetails,
  currentTradeDetails,
  locale,
  staticValues,
  isDataLoading,
  targetAssetData,
  overrideOperationWithVariant,
  openBuyTab,
  isMarketClosed,
}) => {
  const { formState, handlers } = useTradeForm({
    assetDetails,
    targetAssetData,
    currentTradeDetails,
    staticValues,
  });

  const searchParams = useSearchParams();

  const verifyLink = searchParams?.get(VERIFY_LINK_KEY);

  const { savingsPlansFrequencySelection } = getAppConfig();

  const feature_enableFiatTransfers = useFeatureFlag(
    'feature_enableFiatTransfers',
  );

  const isFiatWalletEnabled =
    staticValues.transactionType === 'buy' && feature_enableFiatTransfers;

  const allowAssetSelection = (
    ['buy', 'sell', 'swap', 'stake', 'unstake'] as TradeControllerVariants[]
  ).includes(staticValues.transactionType);

  const hasOrderTargetPrice =
    searchParams.has(TARGET_PRICE_KEY) &&
    staticValues.transactionType === TradeOperation.order;

  const isSellOrderPriceScreen =
    !searchParams.has(TARGET_PRICE_KEY) &&
    staticValues.orderOperation === OrderOperation.SELL &&
    staticValues.transactionType === TradeOperation.order;

  if (verifyLink) {
    return (
      <VerificationLinkForm
        isDataLoading={isDataLoading}
        formState={formState}
        staticValues={staticValues}
        currentTradeDetails={currentTradeDetails}
        verifyLink={verifyLink}
      />
    );
  }

  const submitDisabled =
    isMarketClosed ||
    formState.inputValue?.length === 0 ||
    formState.formHasAnyErrors ||
    formState.allButtonsDisabled ||
    Number(formState.inputValue.replace(',', '.')) === 0;

  const inputTitleByVariant: Partial<Record<TradeControllerVariants, string>> =
    {
      order: hasOrderTargetPrice
        ? staticValues.translations.targetPrice
        : staticValues.orderOperation === OrderOperation.SELL
          ? staticValues.translations.sellAmount
          : staticValues.translations.buyAmount,
    };

  const showQuickSelectButtons =
    isSellOrderPriceScreen ||
    (staticValues.transactionType !== TradeOperation.order
      ? true
      : searchParams.has(TARGET_PRICE_KEY));

  const isSellTrade = staticValues.transactionType === 'sell';

  return (
    <form
      onSubmit={handlers.onFormSubmit}
      className={css({
        flex: 1,
        display: 'flex',
        flexDir: 'column',
        gap: 'medium',
      })}
      data-testid="trade-form"
    >
      <HiddenInputs formState={formState} />

      <div className={css({ display: 'flex', flexDir: 'column' })}>
        <AmountInputWrapper
          inputContextData={formState.inputContextData}
          learnMoreElementSlot={
            hasOrderTargetPrice ? <LearnMoreOrderDrawerButton /> : null
          }
          title={
            inputTitleByVariant[
              staticValues.transactionType as TradeControllerVariants
            ]
          }
        >
          {({ inputRef, modifiedBlur }) => {
            return (
              <AmountInput
                ref={inputRef}
                placeholder={formState.inputPlaceholder}
                value={formState.inputValue}
                disabled={formState.allButtonsDisabled}
                onBlur={modifiedBlur}
                onChange={(e) => handlers.onTradeInputChange(e.target.value)}
              />
            );
          }}
        </AmountInputWrapper>

        {!hasOrderTargetPrice && (
          <div
            className={css({
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 'extra_small_2',
              pt: 'small',
              pb: 'small',
              color: 'neutrals.text_disabled',
              fontSize: 'title.medium_medium',
              fontWeight: 'title.medium_medium',
              lineHeight: 'title.medium_medium',
              letterSpacing: 'title.medium_medium',
            })}
          >
            {isDataLoading ? (
              <>
                <SkeletonElement
                  width="40px"
                  height="40px"
                  borderRadius="50%"
                />
                <SkeletonElement
                  width="120px"
                  height="20px"
                  borderRadius="4px"
                />
              </>
            ) : (
              <>
                {isSellTrade && (
                  <FiatAssetSwitch
                    onChange={() => {
                      handlers.onChangeDisplayType(
                        formState.displayType === 'ASSET' ? 'FIAT' : 'ASSET',
                      );
                    }}
                  />
                )}
                {formState.convertedSummary}
              </>
            )}
          </div>
        )}

        {showQuickSelectButtons && (
          <QuickSelectButtons
            items={currentTradeDetails.quickSelectTradeItems}
            onQuickSelectButtonClick={handlers.onQuickSelectButtonClick}
            disabled={formState.allButtonsDisabled}
            displayType={formState.displayType}
            inputValue={formState.inputValue}
          />
        )}
      </div>

      <Divider />

      <MainTradeFormError
        locale={locale}
        transactionType={staticValues.transactionType}
        errors={formState.errors}
        minimumTradeAmount={currentTradeDetails.minimumTradeAmount}
        customMaxFiatAmount={currentTradeDetails.customMaxFiatAmount}
        openBuyTab={openBuyTab}
        assetDetails={assetDetails}
      />

      {staticValues.transactionType === 'savings' && (
        <div className={css({ paddingTop: 'medium' })}>
          <SetUpSavingsPlanBox
            translations={{
              setUpSavingsPlanBoxTitle:
                staticValues.translations.setUpSavingsPlanBoxTitle,
              setUpSavingsPlanBoxDescription:
                staticValues.translations.setUpSavingsPlanBoxDescription,
              frequency: staticValues.translations.frequency,
              startsAt: staticValues.translations.startsAt,
              monthly: staticValues.translations.MONTHLY,
              biweekly: staticValues.translations.BIWEEKLY,
              weekly: staticValues.translations.WEEKLY,
              daily: staticValues.translations.DAILY,
              ok: staticValues.translations.ok,
              cancel: staticValues.translations.cancel,
              nextRecurrence: staticValues.translations.nextRecurrence,
            }}
            locale={locale}
            recurrenceDate={formState.savingsPlanRecurringDate}
            onChangeDate={handlers.setSavingsPlanRecurringDate}
            showFrequencyDropdown={
              (savingsPlansFrequencySelection?.length ?? 0) > 0
            }
            frequencyOptions={savingsPlansFrequencySelection}
            frequencyValue={formState.savingsPlanFrequency}
            onFrequencyChange={handlers.setSavingsPlanFrequency}
          />
        </div>
      )}

      {allowAssetSelection && (
        <TradeAssetSelector
          locale={locale}
          selectedAssetData={assetDetails as unknown as MutableNonNullableAsset}
          overrideOperationWithVariant={overrideOperationWithVariant}
          targetAssetData={targetAssetData}
          amountInput={formState.inputValue}
        />
      )}

      {isFiatWalletEnabled &&
        !formState.formHasAnyErrors &&
        currentTradeDetails.minimumTradeAmount.FIAT?.value !== undefined && (
          <FiatFeesSection
            amountInput={formState.inputValue}
            currency={staticValues.currency}
            locale={staticValues.currentLocale}
            minimumAmount={currentTradeDetails.minimumTradeAmount.FIAT.value}
          />
        )}

      <TradeFormFooter
        baseLoading={formState.isLoading || isDataLoading}
        baseDisabled={submitDisabled}
        staticValues={staticValues}
      />
    </form>
  );
};

export default memo(MainTradeForm);
